import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Select,
  Button,
  message,
  Spin,
  Descriptions,
  Divider,
  Tag,
  Modal,
  Input,
  Switch,
} from "antd";
import { getChannelTagStyle } from "./NotificationTopicsTable";
import { useUserManagement } from "hooks";
import AsyncSelect from "react-select/async";

const tagStyles = {
  padding: "2px 8px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 500,
  marginRight: 4,
  fontFamily: "Inter",
  marginBottom: 4,
};

const mergeUniqueById = (existing, incoming) => {
  const map = new Map();

  [...existing, ...incoming].forEach((item) => {
    map.set(item.id, item);
  });

  return Array.from(map.values());
};

const TopicAssignmentsDrawer = ({
  visible,
  onClose,
  topic,
  roles,
  // users,
  templates,
  onSubmit,
  loading,
  onRemoveRole,
  onRemoveUser,
  onRemoveTemplate,
}) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const { searchUserProfiles, loading: usersLoading } = useUserManagement();
  const [userOptions, setUserOptions] = useState([]);
  const [localTopic, setLocalTopic] = useState(null);
  const [assignMode, setAssignMode] = useState("roles");
  const [modalVisible, setModalVisible] = useState(false);
  const [modalItems, setModalItems] = useState([]);
  const [modalTitle, setModalTitle] = useState("");


  useEffect(() => {
    if (topic) {
      setLocalTopic(topic);
      form.setFieldsValue({
        roles: [],
        users: [],
        roleDescription: "",
        userDescription: "",
      });
      setAssignMode("roles");
    } else {
      form.resetFields();
      setLocalTopic(null);
    }
  }, [topic]);

  const handleUserSearch = async (value) => {
    if (!value) {
      setUserOptions([]);
      return;
    }

    try {
      const results = await searchUserProfiles(value);

      const options = results
        .filter((u) => !assignedUserIds.includes(u.id))
        .map((u) => ({
          label: `${u.firstName} ${u.lastName} (${u.email})`,
          value: u.id,
          user: u,
        }));

      setUserOptions(options);
    } catch (err) {
      setUserOptions([]);
    }
  };


  const renderLimitedTags = (
    items,
    labelKey = "name",
    onRemove,
    limit = 3,
    type = "",
  ) => {
    if (!items || items.length === 0)
      return <span style={{ color: "#999" }}>None</span>;

    const visibleItems = items.slice(0, limit);
    const remainingItems = items.slice(limit);

    return (
      <>
        {visibleItems.map((item) => (
          <Tag
            key={item.id}
            closable
            style={tagStyles}
            onClose={(e) => {
              e.preventDefault();
              onRemove(item.id);
            }}
          >
            {item[labelKey]}
          </Tag>
        ))}
        {remainingItems.length > 0 && (
          <Tag
            style={{ ...tagStyles, cursor: "default" }}
            onClick={() => {
              setModalItems(remainingItems);
              setModalTitle(type === "roles" ? "All Roles" : "All Users");
              setModalVisible(true);
            }}
          >
            +{remainingItems.length} more
          </Tag>
        )}
      </>
    );
  };

  const assignedRoleIds = localTopic?.roles?.map((r) => r.id) || [];
  const assignedUserIds = localTopic?.users?.map((u) => u.id) || [];

  const handleRemoveRoleLocal = async (roleId) => {
    setLocalTopic((prev) => ({
      ...prev,
      roles: prev.roles.filter((r) => r.id !== roleId),
    }));

    try {
      await onRemoveRole(topic.id, roleId);
    } catch (err) {
      message.error("Failed to remove role");

      // rollback on fail
      setLocalTopic(topic);
    }
  };

  const handleRemoveUserLocal = async (userId) => {
    setLocalTopic((prev) => ({
      ...prev,
      users: prev.users.filter((u) => u.id !== userId),
    }));

    try {
      await onRemoveUser(topic.id, userId);
    } catch (err) {
      message.error("Failed to remove user");

      // rollback
      setLocalTopic(topic);
    }
  };

  const handleRemoveTemplateLocal = async (templateId) => {
    setLocalTopic((prev) => ({
      ...prev,
      template: null,
    }));

    try {
      await onRemoveTemplate(topic.id, templateId);
    } catch (err) {
      message.error("Failed to remove template");
      setLocalTopic(topic);
    }
  };

  const rolesSelected = Form.useWatch("roles", form) || [];
  const usersSelected = Form.useWatch("users", form) || [];
  const selectedTemplateId = Form.useWatch("template", form);

  const isSubmitDisabled = () => {
    if (assignMode === "roles") return rolesSelected.length === 0;
    if (assignMode === "users") return usersSelected.length === 0;
    if (assignMode === "templates") return !selectedTemplateId;

    return true;
  };

  const handleFinish = async (values) => {
    if (!localTopic) return;

    // roles
    if (assignMode === "roles") {
      const selectedRoles =
        roles.filter((r) => values.roles?.includes(r.id)) || [];

      // optimistic update
      setLocalTopic((prev) => ({
        ...prev,
        roles: mergeUniqueById(prev.roles, selectedRoles),
      }));
    }

    // users
    if (assignMode === "users") {
      const selectedUsers =
        users.filter((u) => values.users?.includes(u.id)) || [];

      setLocalTopic((prev) => ({
        ...prev,
        users: mergeUniqueById(prev.users, selectedUsers),
      }));
    }

    // template
    if (assignMode === "templates") {
      const selectedTemplateId = values.template;
      const selectedTemplate =
        templates.find((t) => t.id === selectedTemplateId) || null;

      // optimistic update
      setLocalTopic((prev) => ({
        ...prev,
        template: selectedTemplate ? selectedTemplate : {},
      }));
    }

    try {
      await onSubmit(topic, values, assignMode);

      form.resetFields([
        "roles",
        "users",
        "roleDescription",
        "userDescription",
      ]);
    } catch (err) {
      message.error("Failed to assign");
      setLocalTopic(topic);
    }
  };

  return (
    <Drawer
      title={
        <span style={{ fontFamily: "Inter" }}>
          Manage Assignments - {topic?.name || ""}
        </span>
      }
      size={620}
      zIndex={99999}
      onClose={() => onClose(false)}
      open={visible}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={() => onClose(false)} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            disabled={isSubmitDisabled()}
          >
            {assignMode === "roles"
              ? "Assign Roles"
              : assignMode === "users"
              ? "Assign Users"
              : "Assign Template"}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        {topic && (
          <>
            <Descriptions
              size="small"
              column={1}
              bordered
              style={{ marginBottom: 16, fontFamily: "Inter" }}
            >
              <Descriptions.Item label="Topic Name" style={{ fontSize: 12 }}>
                {topic.name}
              </Descriptions.Item>

              <Descriptions.Item label="Description" style={{ fontSize: 12 }}>
                {topic.description || "-"}
              </Descriptions.Item>

              <Descriptions.Item label="Status" style={{ fontSize: 12 }}>
                <Tag color={topic.enabled ? "green" : "red"} style={tagStyles}>
                  {topic.enabled ? "Enabled" : "Disabled"}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Channels" style={{ fontSize: 12 }}>
                {topic.channels?.length ? (
                  topic.channels.map((ch) => (
                    <Tag key={ch.id} style={getChannelTagStyle(ch.type)}>
                      {ch.type}
                    </Tag>
                  ))
                ) : (
                  <span style={{ color: "#999" }}>No channels</span>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Assigned Roles" style={{ fontSize: 12 }}>
                {renderLimitedTags(
                  localTopic?.roles,
                  "name",
                  handleRemoveRoleLocal,
                  4,
                  "roles",
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Assigned Users" style={{ fontSize: 12 }}>
                {renderLimitedTags(
                  (localTopic?.users || []).map((u) => ({
                    id: u.id,
                    name: u.firstName && u.lastName
                      ? `${u.firstName} ${u.lastName}`
                      : u.name || "Unknown User",
                  })),
                  "name",
                  handleRemoveUserLocal,
                  5,
                  "users",
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Template" style={{ fontSize: 12 }}>
                {localTopic?.template ? (
                  <Tag
                    closable
                    style={tagStyles}
                    onClose={(e) => {
                      e.preventDefault();
                      handleRemoveTemplateLocal(topic.template.id);
                    }}
                  >
                    {localTopic?.template?.code}
                  </Tag>
                ) : (
                  <span style={{ color: "#999" }}>None</span>
                )}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
        <p className="fw-semibold">Update Assignments</p>
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          <Form.Item label="Assignment Type">
            <Select
              value={assignMode}
              onChange={setAssignMode}
              options={[
                { label: "Roles", value: "roles" },
                { label: "Users", value: "users" },
                { label: "Templates", value: "templates" },
              ]}
            />
          </Form.Item>

          {assignMode === "roles" && (
            <>
              <Form.Item label="Roles" name="roles">
                <Select
                  mode="multiple"
                  placeholder="Select roles"
                  options={roles
                    .filter((r) => !assignedRoleIds.includes(r.id))
                    .map((r) => ({
                      label: r.name,
                      value: r.id,
                    }))}
                />
              </Form.Item>
              <Form.Item
                label="Role Assignment Description"
                name="roleDescription"
                rules={[
                  {
                    required: true,
                    message: "Please enter a description for roles",
                  },
                ]}
              >
                <Input.TextArea placeholder="Describe why these roles are being assigned" />
              </Form.Item>
            </>
          )}

          {assignMode === "users" && (
            <>
              <Form.Item label="Users" name="users">
                <Select
                  mode="multiple"
                  showSearch={{filterOption: false, onSearch: handleUserSearch}}
                  placeholder="Search and select users..."
                  options={userOptions}
                  onChange={(values, selectedOptions) => {
                    form.setFieldsValue({ users: values });

                    const selectedUsers =
                      selectedOptions?.map((opt) => opt.user) || [];

                    setLocalTopic((prev) => ({
                      ...prev,
                      users: mergeUniqueById(prev.users || [], selectedUsers),
                    }));
                  }}
                />
              </Form.Item>
              <Form.Item
                label="User Assignment Description"
                name="userDescription"
                rules={[
                  {
                    required: true,
                    message: "Please enter a description for users",
                  },
                ]}
              >
                <Input.TextArea placeholder="Describe why these users are being assigned" />
              </Form.Item>
            </>
          )}

          {assignMode === "templates" && (
            <>
              <Form.Item label="Templates" name="template">
                <Select
                  placeholder="Select template"
                  options={templates?.map((t) => ({
                    label: t.code,
                    value: t.id,
                  }))}
                  value={localTopic?.template?.id}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Spin>

      <Modal
        title={modalTitle}
        open={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        styles={{ bodyStyle: { maxHeight: 400, overflowY: "auto" } }}
      >
        {modalItems.map((item) => (
          <Tag key={item.id} style={{ ...tagStyles, cursor: "default" }}>
            {item.name}
          </Tag>
        ))}
      </Modal>
    </Drawer>
  );
};

export default TopicAssignmentsDrawer;
